import { useState, useEffect } from "react";
import { ShieldCheck, Loader2, ArrowLeft, CreditCard, Check } from "lucide-react";
import { api, BRAND } from "../lib/config";
import { Button } from "./ui/button";

// iter76/84: Customer-facing payment options are STRICTLY Stripe only.
// Offline methods (Zelle, Venmo, Cash App, Apple Pay via iMessage, Cash on
// Arrival / Pay Later) are hidden from customers entirely — they remain
// fully supported in the admin CRM for manual bookings and continue to
// render on historical bookings via PaymentMethodChip / PAY_METHOD_LABEL.
// Apple Pay and Google Pay still appear inside Stripe Checkout when the
// customer's device supports them (auto-detected by Stripe).
const METHODS = [
  { id: "stripe", label: "Credit / Debit Card", brand: "#635BFF", hint: "Visa · Mastercard · Amex · Apple Pay · Google Pay" },
];

// iter174 — hard-coded fallback so the disclosure ALWAYS renders even if
// the /api/payments/autopay-consent request is slow or fails. Backend is
// the source of truth on the version stamp.
const FALLBACK_CONSENT_TEXT = (
  "By completing this payment, you authorize JDSN Lawn Care to securely " +
  "save your payment method and automatically charge it for future " +
  "recurring services until you cancel your recurring service or update " +
  "your payment method. You can manage your payment method at any time " +
  "from your customer account."
);

/**
 * PaymentMethodStep — final step before booking creation. Customer confirms
 * card payment (Stripe Checkout). All offline methods have been removed.
 *
 * Props:
 *   amount — total to charge (number)
 *   onConfirm(methodId, {saveCard}) — called when customer hits Pay
 *   onBack — go back to step 2
 *   submitting — disables the action button while booking POST is in flight
 *   wallet — { brand, last4, autopay, authorizedAt } when the customer
 *            already has a saved card on file (returning customer)
 */
export default function PaymentMethodStep({ amount, onConfirm, onBack, submitting, wallet, bookingNote }) {
  // Only one method now — auto-select it so the customer never has to tap
  // the row header first.
  const [selected, setSelected] = useState(METHODS.length === 1 ? METHODS[0].id : null);
  const amountStr = Number(amount || 0).toFixed(2);
  const hasWallet = !!(wallet && wallet.autopay && wallet.last4);

  // iter177 — Optional tip section shown BEFORE payment. Quick buttons
  // $5 / $10 / $20 / Custom. Never required to complete a booking.
  // "Always add this tip to future recurring services" saves the amount
  // on the customer's profile at checkout — applied to every future
  // recurring AutoPay charge until changed / removed.
  const [tipAmount, setTipAmount] = useState(0);
  const [customTipStr, setCustomTipStr] = useState("");
  const [tipMode, setTipMode] = useState("none"); // "none" | "5" | "10" | "20" | "custom"
  const [alwaysTip, setAlwaysTip] = useState(false);

  const applyPreset = (val, mode) => {
    setTipMode(mode);
    setTipAmount(val);
    if (mode !== "custom") setCustomTipStr("");
  };
  const applyCustom = (raw) => {
    setTipMode("custom");
    setCustomTipStr(raw);
    const n = parseFloat(raw);
    setTipAmount(!isNaN(n) && n >= 0 ? Math.min(500, Math.round(n * 100) / 100) : 0);
  };
  const clearTip = () => { setTipMode("none"); setTipAmount(0); setCustomTipStr(""); setAlwaysTip(false); };

  const totalWithTip = Number((Number(amount || 0) + Number(tipAmount || 0)).toFixed(2));
  const totalStr = totalWithTip.toFixed(2);

  // iter174 — pull the exact disclosure text (and version) from the backend
  // so the copy shown to the customer is always the same as the copy
  // stamped on their user record at authorization time.
  const [consentText, setConsentText] = useState(FALLBACK_CONSENT_TEXT);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payments/autopay-consent");
        if (!cancelled && data?.text) setConsentText(data.text);
      } catch { /* fallback stays */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const confirmPayment = () => onConfirm("stripe", {
    saveCard: true,
    tipAmount: tipAmount > 0 ? tipAmount : 0,
    alwaysTip: !!alwaysTip && tipAmount > 0,
  });

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="payment-method-step">
      <div className="lg:col-span-2 space-y-4">
        {/* iter173 — Customer Wallet auto-attach banner. When the signed-in
            customer already has a saved card + AutoPay enabled, offer a
            one-tap "Book with saved card" primary CTA. The manual Stripe
            path remains available below for edge cases. */}
        {hasWallet && (
          <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-3xl p-5 sm:p-6 shadow-sm"
               data-testid="wallet-auto-attach-banner">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">Your JDSN Wallet</div>
                <div className="mt-0.5 font-extrabold text-gray-900 leading-tight">
                  Book with your saved {wallet.brand ? wallet.brand.toUpperCase() : "card"} •••{wallet.last4}
                </div>
                <div className="mt-1 text-xs text-gray-600 leading-snug">
                  AutoPay is on. We&apos;ll auto-charge this card <span className="font-bold">after</span> the visit completes — no re-entering your card.
                </div>
              </div>
            </div>
            {/* iter174 — Reiterate the previously-recorded authorization
                so the customer can see exactly what they agreed to, every
                time they book. */}
            <div className="mt-4 rounded-xl border border-emerald-100 bg-white/70 p-3 text-[12px] text-emerald-900 leading-snug"
                 data-testid="wallet-authorization-recap">
              {consentText}
            </div>
            <Button
              type="button"
              onClick={confirmPayment}
              disabled={submitting}
              className="mt-4 w-full h-13 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-extrabold text-base shadow-[0_8px_24px_rgba(4,120,87,0.28)]"
              data-testid="pay-confirm-wallet"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
              Book ${totalStr} on AutoPay{tipAmount > 0 ? ` (incl. $${tipAmount.toFixed(2)} tip)` : ""}
            </Button>
            <div className="mt-2 text-[10px] uppercase tracking-[0.14em] font-bold text-gray-500 text-center">
              Cancel anytime · Manage card in Profile
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-7 pb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">Optional · Show the crew some love</div>
            <h2 className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
              Leave a tip <span className="text-gray-400 text-sm font-bold">(optional)</span>
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              100% of tips go straight to the crew who serviced your yard.
            </p>
          </div>
          <div className="p-5 sm:p-7 pt-0" data-testid="tip-section">
            <div className="grid grid-cols-4 gap-2">
              {[
                { val: 5, mode: "5" },
                { val: 10, mode: "10" },
                { val: 20, mode: "20" },
              ].map(({ val, mode }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => applyPreset(val, mode)}
                  className={`h-12 rounded-2xl font-extrabold text-sm border-2 transition ${tipMode === mode ? "bg-[#047857] text-white border-[#047857]" : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300"}`}
                  data-testid={`tip-preset-${mode}`}
                >
                  ${val}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setTipMode("custom"); setTipAmount(0); }}
                className={`h-12 rounded-2xl font-extrabold text-sm border-2 transition ${tipMode === "custom" ? "bg-[#047857] text-white border-[#047857]" : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300"}`}
                data-testid="tip-preset-custom"
              >
                Custom
              </button>
            </div>
            {tipMode === "custom" && (
              <div className="mt-3 flex items-center gap-2" data-testid="tip-custom-row">
                <span className="text-sm font-bold text-gray-500">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0" max="500" step="0.01"
                  value={customTipStr}
                  onChange={(e) => applyCustom(e.target.value)}
                  placeholder="Enter tip amount"
                  className="flex-1 h-11 px-3 rounded-xl border-2 border-gray-200 focus:border-emerald-400 outline-none text-sm font-bold"
                  data-testid="tip-custom-input"
                />
              </div>
            )}
            {tipAmount > 0 && (
              <>
                <label className="mt-4 flex items-start gap-2.5 cursor-pointer p-3 rounded-2xl border border-emerald-100 bg-emerald-50/60"
                       data-testid="always-tip-toggle-label">
                  <input
                    type="checkbox"
                    checked={alwaysTip}
                    onChange={(e) => setAlwaysTip(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-emerald-600"
                    data-testid="always-tip-toggle"
                  />
                  <span className="text-sm text-gray-900 leading-snug">
                    <span className="font-extrabold">Always add this ${tipAmount.toFixed(2)} tip to my future recurring services.</span>
                    <span className="block text-xs text-gray-600 mt-0.5">You can change or remove this anytime in your profile.</span>
                  </span>
                </label>
                <button
                  type="button"
                  onClick={clearTip}
                  className="mt-2 text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500 hover:text-gray-700"
                  data-testid="tip-clear-btn"
                >
                  Remove tip
                </button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 sm:p-7 pb-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">Choose how you&apos;d like to pay</div>
            <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tighter text-gray-900 font-[Outfit]">
              Pay <span className="text-[#047857]">${amountStr}</span> your way
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Secure card checkout powered by Stripe. Apple Pay and Google Pay appear automatically on supported devices.
            </p>
          </div>

          <div className="p-5 sm:p-7 pt-2 space-y-2.5">
            {METHODS.map((m) => {
              const isStripe = m.id === "stripe";
              const expanded = selected === m.id;

              return (
                <div key={m.id} className="rounded-2xl border border-gray-200 overflow-hidden" data-testid={`pay-method-${m.id}`}>
                  <button
                    type="button"
                    onClick={() => setSelected(expanded ? null : m.id)}
                    className={`w-full flex items-center gap-3 p-4 text-left transition ${expanded ? "bg-gray-50" : "bg-white hover:bg-gray-50"}`}
                    data-testid={`pay-method-toggle-${m.id}`}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-extrabold flex-shrink-0" style={{ background: m.brand }}>
                      {isStripe ? <CreditCard className="w-5 h-5" /> : m.label.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-extrabold text-gray-900 leading-tight">{m.label}</div>
                      <div className="text-xs text-gray-500 truncate">{m.hint}</div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${expanded ? "border-[#047857] bg-[#047857]" : "border-gray-300"}`}>
                      {expanded && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </button>

                  {expanded && isStripe && (
                    <div className="px-4 pb-5 pt-1 border-t border-gray-100 bg-white">
                      <div className="mt-3 rounded-xl bg-[#F4F6F5] border border-gray-200 p-4 text-sm text-gray-700 leading-relaxed">
                        Secure card checkout powered by Stripe. You&apos;ll be redirected to enter card details — your booking is created on return.
                      </div>
                      {/* iter174 — MIT-compliant AutoPay authorization
                          disclosure. Payment completion itself is the
                          customer's consent; no separate checkbox. The
                          exact text is fetched from the backend so the
                          audit stamp on user.autopay_consent_text is
                          always aligned with what was shown. */}
                      <div className="mt-4 flex items-start gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/80"
                           data-testid="autopay-authorization-disclosure">
                        <ShieldCheck className="w-5 h-5 text-[#047857] flex-shrink-0 mt-0.5" />
                        <div className="text-sm">
                          <div className="font-extrabold text-emerald-900 leading-tight mb-1">
                            Payment authorization
                          </div>
                          <div className="text-[13px] text-emerald-900/90 leading-relaxed">
                            {consentText}
                          </div>
                        </div>
                      </div>
                      <Button
                        type="button"
                        onClick={confirmPayment}
                        disabled={submitting || Number(amount || 0) <= 0}
                        className="mt-4 w-full h-13 rounded-full bg-gradient-to-br from-[#10B981] to-[#047857] text-white font-extrabold text-base shadow-[0_8px_24px_rgba(4,120,87,0.28)] disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="pay-confirm-stripe"
                      >
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-1.5" />}
                        {Number(amount || 0) <= 0
                          ? "Price loading…"
                          : <>Pay ${totalStr} with Card{tipAmount > 0 ? ` (incl. $${tipAmount.toFixed(2)} tip)` : ""}</>}
                      </Button>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-500">
                        <ShieldCheck className="w-3 h-3" /> Stripe · Visa · Mastercard · Amex · Apple Pay · Google Pay
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-bold text-gray-700 hover:text-gray-900 active:scale-95 transition"
          data-testid="pay-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {/* Summary side */}
      <aside className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 h-fit lg:sticky lg:top-24" data-testid="payment-summary">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-extrabold">Order summary</div>
        <div className="mt-2 text-xl font-extrabold text-gray-900 font-[Outfit]">JDSN Lawn Care</div>
        <div className="mt-1 text-sm text-gray-600">Booking total includes all selected services and yard adjustments.</div>
        {bookingNote && (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2 text-[12px] font-semibold text-emerald-900"
                data-testid="payment-summary-booking-note">
            {bookingNote}
          </div>
        )}
        <div className="mt-4 border-t border-gray-100 pt-4 space-y-1.5">
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span className="font-bold">Services subtotal</span>
            <span className="tabular-nums font-extrabold" data-testid="payment-subtotal">${amountStr}</span>
          </div>
          {tipAmount > 0 && (
            <div className="flex items-center justify-between text-sm text-gray-700" data-testid="payment-tip-line">
              <span className="font-bold">Tip</span>
              <span className="tabular-nums font-extrabold">${tipAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-gray-100 pt-2">
            <span className="font-extrabold text-gray-900">Total due</span>
            <span className="text-3xl font-extrabold text-[#047857] tabular-nums font-[Outfit]" data-testid="payment-total">${totalStr}</span>
          </div>
        </div>
        <p className="mt-4 text-[11px] text-gray-500 leading-relaxed">
          Questions? Text us at{" "}
          <a href={`sms:${BRAND.phoneTel}`} className="font-bold text-[#047857]">{BRAND.phone}</a>.
        </p>
      </aside>
    </div>
  );
}
